// ImageUploader.jsx — Composant de upload image réutilisable
// Usage : <ImageUploader value={url} onChange={(url) => setUrl(url)} label="Logo" />
// Backend : POST /api/upload (multipart/form-data, champ 'file', max 5MB)
import { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * @param {string} value — URL actuelle de l'image (peut être /api/upload/files/X ou URL externe)
 * @param {function} onChange — appelé avec l'URL absolue après upload réussi (ou '' au reset)
 * @param {string} label — label du champ
 * @param {string} testid — base test id
 * @param {string} hint — texte d'aide affiché sous le champ
 * @param {string} aspect — '1/1' | '16/9' | 'auto'
 */
export default function ImageUploader({
  value, onChange, label = 'Image', testid = 'image-uploader',
  hint = 'PNG, JPG, WEBP — 5 Mo max',
  aspect = 'auto',
}) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const absoluteUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
    return url;
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Fichier trop volumineux (max 5 Mo)');
    const ok = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!ok.includes(file.type)) return toast.error('Format non supporté (JPG, PNG, GIF, WEBP, SVG)');

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('assoperf_token');
      const r = await fetch(`${API}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || data.error || 'Erreur upload');
      onChange(data.url);
      toast.success('Image envoyée');
    } catch (err) {
      toast.error(err.message || 'Erreur upload');
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const display = absoluteUrl(value);

  return (
    <div data-testid={testid}>
      <label className="label-uppercase block mb-1.5">{label}</label>
      {display ? (
        <div className="relative inline-block group">
          <img
            src={display} alt={label}
            className={`rounded-lg border border-slate-200 object-cover bg-slate-50 ${aspect === '1/1' ? 'w-24 h-24' : aspect === '16/9' ? 'w-full max-w-md aspect-video' : 'max-h-40 max-w-full'}`}
            data-testid={`${testid}-preview`}
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-500 hover:bg-rose-600 text-white grid place-items-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`${testid}-remove`}
            aria-label="Supprimer l'image"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => !uploading && fileRef.current?.click()}
          className={`border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-[var(--theme-primary)] hover:bg-slate-50 transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
          data-testid={`${testid}-dropzone`}
        >
          {uploading ? (
            <div className="py-2 flex items-center justify-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Envoi…
            </div>
          ) : (
            <>
              <ImageIcon className="w-6 h-6 mx-auto text-slate-400 mb-2" />
              <div className="text-xs font-medium text-slate-700">Cliquez ou déposez une image</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>
            </>
          )}
        </div>
      )}
      <input
        ref={fileRef}
        type="file" accept="image/*" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
